/** @format */
import { useContext, useEffect, useMemo, useState } from 'react';

import { Formik } from 'formik';
import { useLocation, useNavigate } from 'react-router-dom';
import * as Yup from 'yup';

import { EventContext } from '../../Contexts/EventContext';
import ErrorMessage from '../../Shared/Components/ErrorMessage';
import Loading from '../../Shared/Components/Loading';
import { error, success } from '../../Shared/Components/Toast';
import useApi from '../../Shared/hooks/api/Index';
import { usePasswordPolicy } from '../../Shared/hooks/usePasswordPolicy';
import { serverErrorHandler } from '../../Shared/utils/helper';
import { buildNewPasswordFieldSchema } from '../../Shared/utils/passwordPolicy';

const SetPassword = () => {
  const passwordPolicy = usePasswordPolicy();
  const validationSchema = useMemo(
    () =>
      Yup.object().shape({
        nat_code: Yup.string()
          .required('لطفا کد ملی را وارد نمایید')
          .length(10, 'کد ملی باید دقیقاً 10 کاراکتر باشد'),
        password_confirmation: Yup.string()
          .required('لطفا تکرار کلمه عبور را وارد نمایید')
          .oneOf(
            [Yup.ref('password')],
            'تکرار کلمه عبور باید با کلمه عبور یکسان باشد',
          ),
        password: buildNewPasswordFieldSchema(passwordPolicy),
      }),
    [passwordPolicy],
  );

  const [loading, setLoading] = useState(false);
  const [formLoading, setFormLoading] = useState(false);
  const eventContext = useContext(EventContext);
  const [serverErrors, setServerErrors] = useState({});
  const { state } = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (
      state?.nat_code === undefined ||
      state?.nat_code === null ||
      state?.register_token === undefined ||
      state?.register_token === null
    ) {
      navigate('/register', { replace: true });
      return;
    }
  }, [state]);

  const onSubmitLoginForm = async (values, { setSubmitting }) => {
    setSubmitting(true);
    setFormLoading(true);
    const payload = {
      ...values,
      register_token: state?.register_token,
    };

    await useApi.post(
      '/auth/register/password',
      payload,
      (res) => {
        success('ثبت نام با موفقیت انجام شد.');
        setFormLoading(false);
        setSubmitting(false);
        navigate('/login', { replace: true });
      },
      (err) => {
        if (err?.status === 422) {
          serverErrorHandler(err?.data?.errors, setServerErrors);
          setFormLoading(false);
          setSubmitting(false);
        } else {
          error('خطای ناخواسته‌ای رخ داده است');
          setFormLoading(false);
          setSubmitting(false);
        }
      },
    );

    setSubmitting(false);
    setLoading(false);
  };

  return loading ? (
    <Loading />
  ) : (
    <div className='w-100 d-flex flex-column justify-content-center align-items-center bg-purple vh-100'>
      <section className='container'>
        <Formik
          initialValues={{
            nat_code: state?.nat_code,
            password: '',
            password_confirmation: '',
          }}
          validationSchema={validationSchema}
          onSubmit={onSubmitLoginForm}
        >
          {({ values, errors, handleChange, handleSubmit, isSubmitting }) => (
            <form onSubmit={handleSubmit}>
              <div className='row'>
                {/* لوگو و توضیحات */}
                <div className='col-12 d-flex flex-column p-2 text-center'>
                  <img
                    className='w-15 mx-auto'
                    src={eventContext?.setting?.logo?.logo}
                    alt={eventContext?.setting?.name ?? ''}
                  />
                  <h3 className='text-dark-mode f-14 my-3'>
                    فرم زیر را برای تکمیل فرایند تغیر کلمه عبور پر کنید
                  </h3>
                  <div className='d-flex flex-row my-3 align-items-center'>
                    <hr className='w-30 bg-hr' />
                    <span className='text-dark-mode f-14 mx-2'>
                      تغیر کلمه عبور سامانه {eventContext?.setting?.name ?? ''}
                    </span>
                    <hr className='w-30 bg-hr' />
                  </div>
                </div>

                {/* کلمه عبور */}
                <div className='col-12 col-md-6'>
                  <div className='form-group d-flex flex-column'>
                    <label className='text-dark-mode f-15 text-left my-2'>
                      کلمه عبور
                    </label>
                    <input
                      type='password'
                      name='password'
                      value={values.password}
                      onChange={handleChange}
                      className='text-center dir-ltr form-control bg-dark-mode border-0 rounded-1 f-14 text-white'
                    />
                    <div className='d-flex flex-column'>
                      <span className='text-white f-15 mt-2'>
                        برای کلمه عبور رعایت نکات زیر ضروری است :
                      </span>
                      <span className='text-white f-13 mt-2'>
                        - کلمه عبور حداقل باید 10 کاراکتر باشد
                      </span>
                      <span className='text-white f-13 mt-2'>
                        - کلمه عبور باید شامل حداقل یک حرف کوچک انگلیسی یک حرف
                        بزرگ انگلیسی و یک عدد باشد
                      </span>
                      <span className='text-white f-13 mt-2'>
                        - کلمه عبور باید شامل حداقل یک علامت خاص مثل
                        !@#$%^&*()_+ باشد
                      </span>
                    </div>
                    <ErrorMessage
                      error={
                        errors?.password
                          ? errors?.password
                          : serverErrors?.password
                      }
                      classes='my-2 f-13'
                    />
                  </div>
                </div>
                <div className='col-12 col-md-6'>
                  <div className='form-group d-flex flex-column'>
                    <label className='text-dark-mode f-15 text-left my-2'>
                      تکرار کلمه عبور
                    </label>
                    <input
                      type='password'
                      name='password_confirmation'
                      value={values.password_confirmation}
                      onChange={handleChange}
                      className='text-center dir-ltr form-control bg-dark-mode border-0 rounded-1 f-14 text-white'
                    />
                    <ErrorMessage
                      error={
                        errors?.password_confirmation
                          ? errors?.password_confirmation
                          : serverErrors?.password_confirmation
                      }
                      classes='my-2 f-13'
                    />
                  </div>
                </div>

                {/* دکمه ورود */}
                <div className='col-12 col-md-6 offset-md-3'>
                  <div className='form-group d-flex flex-column'>
                    <button
                      type='submit'
                      className='btn btn-info rounded-1 my-2'
                      disabled={isSubmitting}
                    >
                      {formLoading ? (
                        <div
                          className='spinner-border spinner-border-sm text-white'
                          role='status'
                        />
                      ) : (
                        'تکمیل فرایند'
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </form>
          )}
        </Formik>
      </section>
    </div>
  );
};

export default SetPassword;
